import { EditLocationAltOutlined, KeyboardArrowUpOutlined } from "@mui/icons-material";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import LocationOffOutlined from "@mui/icons-material/LocationOffOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { Button, Chip, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { amber, blue, grey } from "@mui/material/colors";
import { get } from "lodash";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import { formatAddressObjectFullAddress } from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import { VALIDATOR_REQUIRE } from "../../../utils/finalForm/validators/VALIDATOR_REQUIRE";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import RffInputWrapper from "../shared/RffInputWrapper";
import RffSelectAutocompleteField from "./RffSelectAutocompleteField";
import RffTextField from "./RffTextField";
import { geocodeZipCentroid } from "../../../utils/geocode/geocodeZipCentroid";
// The Places search, the place -> address mapping and the manual-entry
// helpers are SHARED with the controlled `GoogleAddressInput` used by the
// panel-form sidebars, so the two can never disagree about what an address is.
import {
  assembleFullAddress,
  fetchPlaceDetails,
  mapPlaceDetailsToAddress,
  parseSeedFromQuery,
  searchPlaces,
} from "../../../utils/geocode/googlePlaces";

const RffAddressEditingFieldGoogle = ({
  name,
  label,
  required = false,
  suppressGrid,
  onChange,
  size,
  sx,
  // When true, expose the "Enter address manually" override path. The
  // override geocodes to the ZIP centroid and stamps `manualEntry: true`
  // on the address — used by property addresses for raw-land / new-
  // subdivision cases where Places returns nothing useful. Off by default
  // so non-property usages (borrower, contact) keep the strict-match
  // behavior.
  allowManualEntry = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [manualEntryMode, setManualEntryMode] = useState(false);
  const [manualGeocoding, setManualGeocoding] = useState(false);
  const [manualError, setManualError] = useState(null);
  const form = useForm();

  const validate = useMemo(
    () => (required ? VALIDATOR_REQUIRE : undefined),
    [required],
  );

  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setOptions([]);
      setShowNoResults(false);
      return;
    }
    setLoading(true);
    setShowNoResults(false);
    const suggestions = await searchPlaces(query);
    setOptions(suggestions);
    setShowNoResults(suggestions.length === 0);
    setLoading(false);
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(searchAddresses, 500),
    [searchAddresses],
  );

  useEffect(() => {
    if (inputValue) {
      debouncedSearch(inputValue);
    } else {
      setOptions([]);
      setShowNoResults(false);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === "input" || reason === "clear" || reason === "reset") {
      setInputValue(newInputValue);

      if (event?.nativeEvent?.inputType === "insertFromPaste") {
        searchAddresses(newInputValue);
      }
    }
  };

  const handleChange = (input) => async (event, selectedOption) => {
    if (!selectedOption || !selectedOption.placeId) return;

    try {
      setLoading(true);
      const place = await fetchPlaceDetails(selectedOption.placeId);
      const address = mapPlaceDetailsToAddress(place, selectedOption.id);
      input.onChange(address);
      setInputValue(address.fullAddress);
      setIsEditing(false);
      if (onChange) {
        onChange(address);
      }
    } catch (err) {
      console.error("Google place details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (input) => () => {
    setIsEditing(true);
    setInputValue(input.value?.fullAddress || "");
    input.onChange(null);
  };

  const handleDelete = (input) => () => {
    input.onChange(null);
    setInputValue("");
    setOptions([]);
    setShowNoResults(false);
    setIsEditing(false);
    setManualEntryMode(false);
    setManualError(null);
  };

  // Enter manual-entry mode without an existing value. Seed the parent
  // address with whatever the user typed in the search box (best-effort
  // street parsing) so the form's street fields aren't blank. The ZIP
  // centroid geocode happens on confirm.
  const handleEnterManualEntry = (input) => () => {
    setManualEntryMode(true);
    setIsEditing(false);
    setManualError(null);
    setOptions([]);
    setShowNoResults(false);
    const seed = parseSeedFromQuery(inputValue);
    input.onChange({
      ...seed,
      manualEntry: true,
      geocodeQuality: null, // not yet geocoded
    });
  };

  // Confirm the manually entered fields: geocode the ZIP to get a
  // centroid + estimated radius, then assemble the canonical address
  // shape and stamp manualEntry / geocodeQuality. The address sub-fields
  // (streetNumber, streetName, city, state, zip) are read straight from
  // the form state since the user typed them via Rff* sub-fields.
  const handleConfirmManualEntry = (input) => async () => {
    const current = get(form.getState().values, name) || {};
    const zip = current.zip;
    if (!zip || !/^\d{5}(-\d{4})?$/.test(String(zip).trim())) {
      setManualError("A valid 5-digit ZIP is required to confirm.");
      return;
    }
    setManualGeocoding(true);
    setManualError(null);
    const centroid = await geocodeZipCentroid(zip);
    setManualGeocoding(false);
    if (!centroid) {
      setManualError(
        "Could not look up that ZIP. Double-check it or try again.",
      );
      return;
    }

    const assembledFull = assembleFullAddress(current);
    const next = {
      ...current,
      id: current.id || `manual-${Date.now()}`,
      fullAddress: assembledFull,
      country: current.country || "United States",
      countryCode: current.countryCode || "US",
      latitude: centroid.latitude,
      longitude: centroid.longitude,
      coordinateTuple: [centroid.latitude, centroid.longitude],
      manualEntry: true,
      geocodeQuality: "zip",
      zipCentroid: {
        latitude: centroid.latitude,
        longitude: centroid.longitude,
      },
      zipRadiusMiles: centroid.radiusMiles,
    };
    input.onChange(next);
    setManualEntryMode(false);
    setInputValue(assembledFull);
    if (onChange) onChange(next);
  };

  const renderNoResults = (input) => (
    <Paper elevation={0} sx={{ p: 2, textAlign: "center" }}>
      <LocationOffOutlined sx={{ fontSize: 40, color: grey[400], mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No US addresses found for "{inputValue}"
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Try adding more details like city or state
      </Typography>
      {allowManualEntry && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<EditLocationAltOutlined />}
            onMouseDown={(e) => {
              // Autocomplete closes on blur — mousedown beats blur and
              // keeps the dropdown's onClick from triggering its own
              // selection logic.
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleEnterManualEntry(input)();
            }}
            sx={{ textTransform: "none" }}
          >
            Enter address manually
          </Button>
        </Box>
      )}
    </Paper>
  );

  // Manual-entry render: identical structure to the in-place edit form
  // but with a "Confirm manual entry" CTA at the bottom. Until the user
  // confirms, the address has no fullAddress / lat / lng, so we keep
  // hasValue out of the picture by gating on `manualEntryMode` upstream.
  const renderManualEntry = (input) => (
    <Box
      sx={{
        border: `1px solid ${amber[300]}`,
        borderRadius: 1,
        overflow: "hidden",
        background: amber[50],
      }}
    >
      <Flx
        ac
        jb
        sx={{
          px: "14px",
          py: "10px",
          borderBottom: `1px solid ${amber[200]}`,
        }}
      >
        <Flx ac g={1}>
          <EditLocationAltOutlined sx={{ fontSize: 18, color: amber[800] }} />
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: amber[900] }}>
            Manual address entry
          </Txt>
        </Flx>
        <Tooltip title="Cancel manual entry">
          <IconButton size="small" onClick={handleDelete(input)}>
            <DeleteOutlined className="thin-7" />
          </IconButton>
        </Tooltip>
      </Flx>
      <Flx
        column
        gap={2}
        sx={{
          p: "14px",
          background: "#fff",
        }}
      >
        <Txt sx={{ fontSize: 12, color: grey[700] }}>
          Use this when Google can't find the address (raw land, new
          subdivisions). We'll geocode the ZIP for an approximate map
          location — you can refine the precise spot from the property's
          aerial view.
        </Txt>
        <FlxGrid gap={2} grow={1} gridTemplateColumns={"120px 1fr"}>
          <RffTextField
            suppressGrid
            size={4}
            name={`${name}.streetNumber`}
            label={"Street Number"}
          />
          <RffTextField
            suppressGrid
            size={8}
            name={`${name}.streetName`}
            label={"Street Name"}
          />
        </FlxGrid>
        <Flx
          gap={2}
          wrap
          sx={{
            ".input-wrapper-root": {
              flexGrow: 1,
              flexShrink: 0,
              flexBasis: "300px",
              width: "46%",
            },
          }}
        >
          <RffTextField
            suppressGrid
            size={6}
            name={`${name}.unit`}
            label={"Apartment, Suite, Unit, etc..."}
          />
          <RffTextField
            suppressGrid
            size={6}
            name={`${name}.city`}
            label={"City"}
          />
          <RffSelectAutocompleteField
            suppressGrid
            size={6}
            name={`${name}.state`}
            label={"State"}
            options={selectOptionsUsaStates}
          />
          <RffTextField
            suppressGrid
            size={6}
            name={`${name}.zip`}
            label={"Zip (required)"}
          />
        </Flx>
        {manualError && (
          <Txt sx={{ fontSize: 12, color: "error.main" }}>{manualError}</Txt>
        )}
        <Flx ac jb wrap g={1}>
          <Txt sx={{ fontSize: 11, color: grey[600] }}>
            Confirming will geocode the ZIP centroid as the approximate
            location.
          </Txt>
          <Button
            variant="contained"
            size="small"
            onClick={handleConfirmManualEntry(input)}
            disabled={manualGeocoding}
            startIcon={
              manualGeocoding ? (
                <CircularProgress size={14} />
              ) : (
                <EditLocationAltOutlined />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {manualGeocoding ? "Geocoding ZIP…" : "Confirm manual entry"}
          </Button>
        </Flx>
      </Flx>
    </Box>
  );

  const renderSelectedAddress = (selectedAddress, input) => (
    <Box
      sx={{
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Flx gap={0.5} ac fw jb>
        <Flx ac gap={1} sx={{ flex: 1 }}>
          <Box
            onClick={handleEdit(input)}
            sx={{
              flex: 1,
              cursor: "text",
              p: "12px 14px",
              "&:hover": { background: grey[50] },
            }}
          >
            <Txt sx={{ color: grey[800], fontWeight: 500 }}>
              <RffDisplayedFullyAddress name={name} />
            </Txt>
            {selectedAddress?.manualEntry && (
              <Chip
                size="small"
                icon={
                  <EditLocationAltOutlined
                    sx={{ fontSize: "12px !important" }}
                  />
                }
                label={
                  selectedAddress.geocodeQuality === "pin"
                    ? "Manual entry · pinned"
                    : "Manual entry · ZIP-level"
                }
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: 10.5,
                  bgcolor: amber[50],
                  color: amber[900],
                  border: `1px solid ${amber[200]}`,
                  "& .MuiChip-icon": {
                    color: `${amber[800]} !important`,
                    ml: "6px",
                  },
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Box>
        </Flx>
        <Flx gap={0} sx={{ px: "14px", pr: "8.5px " }}>
          {isEditing ? (
            <Tooltip title="Cancel edit">
              <IconButton
                onClick={() => setIsEditing(false)}
                size="small"
                color="primary"
                sx={{ background: blue[50] }}
              >
                <KeyboardArrowUpOutlined className="thin-7" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Edit address">
              <IconButton onClick={() => setIsEditing(true)} size="small">
                <EditOutlined className="thin-7" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Clear address">
            <IconButton
              onClick={handleDelete(input)}
              size="small"
              color="error"
            >
              <DeleteOutlined className="thin-7" />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      {isEditing && (
        <Flx
          column
          gap={2}
          sx={{
            p: "14px",
            borderTop: `1px solid rgba(0, 0, 0, 0.23)`,
          }}
        >
          <FlxGrid gap={2} grow={1} gridTemplateColumns={"120px 1fr"}>
            <RffTextField
              suppressGrid
              size={4}
              name={`${name}.streetNumber`}
              label={"Street Number"}
            />
            <RffTextField
              suppressGrid
              size={8}
              name={`${name}.streetName`}
              label={"Street Name"}
            />
          </FlxGrid>
          <Flx
            gap={2}
            wrap
            sx={{
              ".input-wrapper-root": {
                flexGrow: 1,
                flexShrink: 0,
                flexBasis: "300px",
                width: "46%",
              },
            }}
          >
            <RffTextField
              suppressGrid
              size={6}
              name={`${name}.unit`}
              label={"Apartment, Suite, Unit, etc..."}
            />
            <RffTextField
              suppressGrid
              size={6}
              name={`${name}.city`}
              label={"City"}
            />
            <RffSelectAutocompleteField
              suppressGrid
              size={6}
              name={`${name}.state`}
              label={"State"}
              options={selectOptionsUsaStates}
            />
            <RffTextField
              suppressGrid
              size={6}
              name={`${name}.zip`}
              label={"Zip"}
            />
          </Flx>
        </Flx>
      )}
    </Box>
  );

  const renderAutocomplete = (input, meta) => (
    <Box>
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      open={inputValue.length >= 3 && (options.length > 0 || showNoResults)}
      getOptionLabel={(option) => option.fullAddress || ""}
      getOptionKey={(option) => option.id}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange(input)}
      noOptionsText={null}
      loadingText="Searching addresses..."
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">
              {option.mainText || option.fullAddress}
            </Typography>
            {option.secondaryText && (
              <Typography variant="caption" color="text.secondary">
                {option.secondaryText}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Enter an address (e.g., 123 Main St, Boston, MA)"
          error={Boolean(meta.touched && meta.error)}
          helperText={meta.touched && meta.error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment sx={{ ml: 0.5, mr: 0 }} position="start">
                <SearchOutlined sx={{ color: grey[400] }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{ width: "100%", ...sx }}
        />
      )}
      PaperComponent={({ children, ...props }) => (
        <Paper {...props}>
          {children}
          {showNoResults && !loading && renderNoResults(input)}
        </Paper>
      )}
    />
    {allowManualEntry && (
      <Flx ac jc="flex-end" sx={{ mt: 0.5, pr: 0.5 }}>
        <Button
          size="small"
          variant="text"
          startIcon={
            <EditLocationAltOutlined sx={{ fontSize: "14px !important" }} />
          }
          onClick={handleEnterManualEntry(input)}
          sx={{
            fontSize: 11.5,
            textTransform: "none",
            color: grey[700],
            py: 0.25,
            "&:hover": { color: amber[900], background: amber[50] },
          }}
        >
          Can't find your address? Enter manually
        </Button>
      </Flx>
    )}
    </Box>
  );

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      required={required}
    >
      <Field
        name={name}
        validate={validate}
        subscription={{ value: true, error: true, touched: true }}
        render={({ input, meta }) => {
          const hasValue = Boolean(input.value?.fullAddress);
          if (manualEntryMode) return renderManualEntry(input);
          return hasValue
            ? renderSelectedAddress(input.value, input)
            : renderAutocomplete(input, meta);
        }}
      />
    </RffInputWrapper>
  );
};

const RffDisplayedFullyAddress = ({ name }) => {
  const { values } = useFormState();
  const value = useMemo(() => {
    const addressObj = get(values, name);
    // Prefer the canonical `fullAddress` recorded on the address (set by
    // Google Places on selection, or assembled on manual-entry confirm).
    // Fall back to composing from parts only when fullAddress is absent —
    // composing-from-parts can drift if individual sub-fields were edited
    // out-of-band on the backend.
    return formatAddressObjectFullAddress(addressObj, true);
  }, [values, name]);
  return value;
};

// Best-effort split of a free-text search query into street parts so the
// manual-entry form isn't completely empty when the user falls back to
// it. Pulls "123 Main St" from "123 Main St, City, ST 12345" if commas
// are present; otherwise drops the whole string into streetName.

export default RffAddressEditingFieldGoogle;
